import { useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import {
  combineDescribedBy,
  combineLabelledBy,
  validateDOMAttributes,
} from '../../shared/component-helper'
import Button from '../button/Button'
import Tooltip from '../tooltip/Tooltip'
import { useSliderEvents } from './hooks/useSliderEvents'
import { useSliderProps } from './hooks/useSliderProps'
import { clamp, getFormattedNumber } from './SliderHelpers'

export function SliderThumb() {
  const { values } = useSliderProps()

  return (
    <>
      {values.map((value, i) => {
        return <Thumb key={i} value={value} currentIndex={i} />
      })}
    </>
  )
}

type ThumbProps = {
  value: number
  currentIndex: number
}

function Thumb({ value, currentIndex }: ThumbProps) {
  const {
    thumbIndex,
    isVertical,
    isReverse,
    showStatus,
    attributes,
    allProps,
    shouldAnimate,
  } = useSliderProps()

  const {
    id,
    label,
    min,
    max,
    step,
    skeleton,
    disabled,
    suffix,
    numberFormat,
    tooltip,
    alwaysShowTooltip,
  } = allProps

  const index = thumbIndex.current
  let percent = clamp(((value - min) * 100) / (max - min))

  if (isReverse) {
    percent = 100 - percent
  }

  const style = {
    zIndex: index === currentIndex ? 4 : 3,
    [`${isVertical ? 'top' : 'left'}`]: `${percent}%`,
  } as CSSProperties

  const {
    onThumbMouseDownHandler,
    onThumbMouseUpHandler,
    onHelperChangeHandler,
    onHelperFocusHandler,
  } = useSliderEvents()

  const { number, aria } = getFormattedNumber(value, numberFormat)

  const helperParams: Record<string, unknown> = {}

  if (label) {
    helperParams['aria-labelledby'] = combineLabelledBy(
      helperParams,
      label ? id + '-label' : null
    )
  }

  if (showStatus || suffix) {
    helperParams['aria-describedby'] = combineDescribedBy(
      helperParams,
      showStatus ? id + '-status' : null,
      suffix ? id + '-suffix' : null
    )
  }

  const thumbParams = attributes as Record<string, unknown>
  const elemRef = useRef<HTMLElement>(undefined)
  const [forceActive, setForceActive] = useState(false)
  validateDOMAttributes(allProps, thumbParams) // because we send along rest attributes

  return (
    <span className="dnb-slider__thumb" style={style}>
      <input
        id={`${id}-thumb-${currentIndex}`}
        type="range"
        className="dnb-slider__button-helper"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={onHelperChangeHandler}
        onFocus={(event) => {
          onHelperFocusHandler(event)
          setForceActive(true)
        }}
        onBlur={() => setForceActive(false)}
        onMouseDown={onThumbMouseDownHandler}
        onMouseUp={onThumbMouseUpHandler}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={aria ? aria : undefined}
        aria-orientation={isVertical ? 'vertical' : 'horizontal'}
        data-index={currentIndex}
        {...helperParams}
      />

      <Button
        id={`${id}-button-${currentIndex}`}
        element="span"
        type="" // avoid getting type="button"
        variant="secondary"
        disabled={disabled}
        skeleton={skeleton}
        ref={elemRef}
        {...thumbParams}
      />

      {tooltip && (
        <Tooltip
          key={`group-${currentIndex}`}
          targetElement={elemRef}
          open={alwaysShowTooltip || forceActive || undefined}
          targetRefreshKey={value}
          showDelay={1}
          hideDelay={300}
          omitDescribedBy
        >
          {number || value}
          {
            shouldAnimate /* Use "shouldAnimate" only in order to update the position after the thumb animation */
          }
        </Tooltip>
      )}
    </span>
  )
}
